export type PError = {
  data?: string;
  name?: string;
  retcode?: string;
  retmsg?: string;
};

/**
 * 将promise执行结果格式化成[error, data]
 * @param promise promise实例
 */
export async function to<P extends Promise<any> = Promise<any>>(promise: P) {
  try {
    const data = await promise;
    return [null, data] as [null, P extends Promise<infer R> ? R : any];
  } catch (err) {
    return [err, null] as [PError, null];
  }
}

/**
 * 获取promise实例及句柄
 */
export function getPromiseHandler<T = any>() {
  let resolve; let reject;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    resolve: resolve as (value: T | PromiseLike<T>) => void,
    reject: reject as (reason?: any) => void,
    promise,
  };
}

/**
 * 给传入的函数添加锁，多次调用只允许同时运行一个
 */
export function lockAsync<T extends (...args: any[]) => Promise<any>>(func: T): T {
  let isLock = false;

  const lockedFunction = async (...args: Parameters<T>): Promise<ReturnType<T> | void> => {
    if (isLock) return;
    isLock = true;
    try {
      return await func(...args);
    } catch (error) {
      throw error;
    } finally {
      isLock = false;
    }
  };

  return lockedFunction as T;
};


export interface LoopQueryOptions<T> {
  // 发起请求的函数
  request: () => Promise<T>;
  // 达到终态的判断函数（返回 true 则停止并 resolve）
  until: (data: T) => boolean;
  // 间隔毫秒，默认 1500
  interval?: number;
  // 最大尝试次数（包含首次），默认 Infinity
  maxAttempts?: number;
}

export interface LoopQueryHandle<T> {
  // 主动停止（不会触发 resolve/reject，由外层自行处理）
  stop: () => void;
  // 轮询最终结果 Promise
  promise: Promise<T>;
}

/**
 * 精简轮询：固定间隔 + 最大次数 + 可外部 stop
 * - 请求报错会继续重试，直至达到 maxAttempts，届时以最后一次错误 reject
 */
export function loopQuery<T>(opts: LoopQueryOptions<T>): LoopQueryHandle<T> {
  const {
    request,
    until,
    interval = 1500,
    maxAttempts = Infinity,
  } = opts;

  if (typeof request !== 'function' || typeof until !== 'function') {
    throw new Error('loopQuery: 必须提供 request 与 until 函数');
  }

  let stopped = false;
  let attempt = 0;
  let timer: number | null = null;

  const clearWait = () => {
    if (timer !== null) {
      clearTimeout(timer as any);
      timer = null;
    }
  };

  const stop = () => {
    stopped = true;
    clearWait();
  };

  const promise = new Promise<T>((resolve, reject) => {
    const tick = async () => {
      /* istanbul ignore next */
      if (stopped) return;
      attempt += 1;

      try {
        const data = await request();

        if (until(data) || attempt >= maxAttempts) {
          stop();
          resolve(data);
          return;
        }

        timer = setTimeout(() => {
          timer = null;
          if (!stopped) void tick();
        }, interval) as unknown as number;
      } catch (err) {
        if (attempt >= maxAttempts) {
          stop();
          reject(err);
          return;
        }

        timer = setTimeout(() => {
          timer = null;
          if (!stopped) void tick();
        }, interval) as unknown as number;
      }
    };

    void tick();
  });

  return { stop, promise };
}
